import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardService } from '../utils/dashboardService';

/**
 * Home component that automatically redirects users to the current active event.
 * This implements the requirement that the home page should always take users
 * to the current active event.
 */
const Home: React.FC = () => {
  const { token, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const redirectToCurrentEvent = async () => {
      // Wait for authentication to be fully established
      if (!token || !isAuthenticated || !user) {
        console.log('🔄 Waiting for authentication to complete...', {
          hasToken: !!token,
          isAuthenticated,
          hasUser: !!user
        });
        setLoading(true);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('🎯 Authenticated user redirecting to current event...', user.email);

        // Try to get the current active event
        const currentEvent = await DashboardService.getCurrentEvent(token);
        
        if (currentEvent?.event_id) {
          console.log(`✅ Redirecting to current active event: ${currentEvent.name} (${currentEvent.event_id})`);
          navigate(`/events/${currentEvent.event_id}`, { replace: true });
          return;
        }

        // If no current event, fall back to dashboard
        console.log('ℹ️ No active event found, redirecting to dashboard');
        navigate('/dashboard', { replace: true });

      } catch (err: any) {
        console.error('❌ Failed to get current event:', err);
        
        // If no active event exists (404), redirect to dashboard
        if (err.message?.includes('No active event') || err.status === 404) {
          console.log('ℹ️ No active event available, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
        } else {
          // For other errors, show error state briefly then redirect to dashboard
          console.error('⚠️ Unexpected error, showing error briefly then redirecting to dashboard');
          setError(err.message || 'Failed to load current event');
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    redirectToCurrentEvent();
  }, [token, isAuthenticated, user, navigate]);

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Finding current hackathon event...</p>
          {user && <p style={{ fontSize: '14px', color: '#666' }}>Welcome, {user.display_name}</p>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
        <div className="error-container">
          <h2>❌ Loading Error</h2>
          <p>{error}</p>
          <p style={{ color: '#666', fontSize: '14px' }}>Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  // This component should always redirect, so this shouldn't be reached
  return (
    <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
      <p>Redirecting...</p>
    </div>
  );
};

export default Home;
